import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Handshake, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AffiliateLoginPage() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Login fields
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");

  // Register fields
  const [reg, setReg] = useState({
    name: "", email: "", cpf_cnpj: "", phone: "", pix: "",
    login: "", password: "", confirmPassword: "", payment_day: 10
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!login || !password) { toast.error("Preencha todos os campos"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/affiliate/login`, { login, password });
      localStorage.setItem("affiliate_token", res.data.token);
      localStorage.setItem("affiliate_user", JSON.stringify(res.data.user));
      toast.success(`Bem-vindo, ${res.data.user.name}!`);
      navigate("/indica");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao fazer login");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!reg.name || !reg.login || !reg.password || !reg.email || !reg.phone) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }
    // Validar espaços no início ou fim do login e senha
    if (reg.login !== reg.login.trim()) {
      toast.error("O login não pode ter espaços no início ou no final");
      return;
    }
    if (reg.password !== reg.password.trim()) {
      toast.error("A senha não pode ter espaços no início ou no final");
      return;
    }
    if (reg.password.length < 4) {
      toast.error("A senha precisa ter pelo menos 4 caracteres");
      return;
    }
    if (reg.password !== reg.confirmPassword) {
      toast.error("As senhas não conferem. Digite a mesma senha nos dois campos.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/affiliate/register`, reg);
      localStorage.setItem("affiliate_token", res.data.token);
      localStorage.setItem("affiliate_user", JSON.stringify(res.data.user));
      toast.success("Cadastro realizado com sucesso!");
      navigate("/indica");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to landing */}
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar ao site
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#1CA1BA]/20 mb-4">
            <Handshake className="w-8 h-8 text-[#1CA1BA]" />
          </div>
          <h1 className="text-3xl font-bold text-white">CiaPonto <span className="text-[#1CA1BA]">Indica+</span></h1>
          <p className="text-slate-400 mt-2">{isRegister ? "Crie sua conta de afiliado" : "Painel do Afiliado"}</p>
        </div>

        {!isRegister ? (
          /* ========== LOGIN ========== */
          <Card className="border-slate-700 bg-slate-800/50 backdrop-blur" data-testid="affiliate-login-card">
            <CardHeader>
              <CardTitle className="text-white">Acesse sua conta</CardTitle>
              <CardDescription className="text-slate-400">Entre com seu login e senha de afiliado</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Login</Label>
                  <Input
                    value={login} onChange={(e) => setLogin(e.target.value)}
                    placeholder="Seu login"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="affiliate-login-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Senha</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Sua senha"
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 pr-10"
                      data-testid="affiliate-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#1CA1BA] hover:bg-[#17889E] text-white"
                  disabled={loading} data-testid="affiliate-login-button">
                  {loading ? "Entrando..." : "Entrar"}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <p className="text-slate-400 text-sm">Ainda não é afiliado?</p>
                <button onClick={() => setIsRegister(true)}
                  className="text-[#1CA1BA] hover:text-[#17889E] font-medium text-sm mt-1 transition-colors"
                  data-testid="go-to-register-btn">
                  Cadastre-se aqui
                </button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* ========== CADASTRO ========== */
          <Card className="border-slate-700 bg-slate-800/50 backdrop-blur" data-testid="affiliate-register-card">
            <CardHeader>
              <CardTitle className="text-white">Criar conta de afiliado</CardTitle>
              <CardDescription className="text-slate-400">Preencha seus dados para se tornar um afiliado CiaPonto Indica+</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-slate-300">Nome Completo *</Label>
                  <Input value={reg.name} onChange={(e) => setReg({...reg, name: e.target.value})}
                    placeholder="Seu nome completo"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="reg-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-slate-300">Email *</Label>
                    <Input type="email" value={reg.email} onChange={(e) => setReg({...reg, email: e.target.value})}
                      placeholder="email@exemplo.com"
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="reg-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-300">Telefone *</Label>
                    <Input value={reg.phone} onChange={(e) => setReg({...reg, phone: e.target.value})}
                      placeholder="(00) 00000-0000"
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="reg-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-slate-300">CPF/CNPJ</Label>
                    <Input value={reg.cpf_cnpj} onChange={(e) => setReg({...reg, cpf_cnpj: e.target.value})}
                      placeholder="000.000.000-00"
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="reg-cpf" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-300">Chave PIX</Label>
                    <Input value={reg.pix} onChange={(e) => setReg({...reg, pix: e.target.value})}
                      placeholder="Para receber comissões"
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="reg-pix" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-slate-300">Login *</Label>
                    <Input value={reg.login} onChange={(e) => setReg({...reg, login: e.target.value})}
                      placeholder="Crie seu login (sem espaços)"
                      className={`bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 ${reg.login && reg.login !== reg.login.trim() ? "border-red-400" : ""}`}
                      data-testid="reg-login" />
                    {reg.login && reg.login !== reg.login.trim() && (
                      <p className="text-xs text-red-400">⚠️ Não use espaços no início ou fim</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-300">Senha *</Label>
                    <Input type="password" value={reg.password} onChange={(e) => setReg({...reg, password: e.target.value})}
                      placeholder="Mínimo 4 caracteres"
                      className={`bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 ${reg.password && reg.password !== reg.password.trim() ? "border-red-400" : ""}`}
                      data-testid="reg-password" />
                    {reg.password && reg.password !== reg.password.trim() && (
                      <p className="text-xs text-red-400">⚠️ Não use espaços no início ou fim</p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300">Confirmar Senha *</Label>
                  <Input type="password" value={reg.confirmPassword} onChange={(e) => setReg({...reg, confirmPassword: e.target.value})}
                    placeholder="Repita exatamente a senha acima"
                    className={`bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 ${
                      reg.confirmPassword && reg.password !== reg.confirmPassword
                        ? "border-red-400"
                        : reg.confirmPassword && reg.password === reg.confirmPassword
                        ? "border-green-400"
                        : ""
                    }`}
                    data-testid="reg-confirm-password" />
                  {reg.confirmPassword && reg.password !== reg.confirmPassword && (
                    <p className="text-xs text-red-400">As senhas não conferem</p>
                  )}
                  {reg.confirmPassword && reg.password === reg.confirmPassword && reg.password.length >= 4 && (
                    <p className="text-xs text-green-400">✓ Senhas conferem</p>
                  )}
                </div>
                <Button type="submit" className="w-full bg-[#1CA1BA] hover:bg-[#17889E] text-white mt-2"
                  disabled={
                    loading ||
                    !reg.name || !reg.login || !reg.password || !reg.email || !reg.phone ||
                    reg.login !== reg.login.trim() ||
                    reg.password !== reg.password.trim() ||
                    reg.password.length < 4 ||
                    reg.password !== reg.confirmPassword
                  }
                  data-testid="affiliate-register-button">
                  {loading ? "Cadastrando..." : "Criar Conta"}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <p className="text-slate-400 text-sm">Já tem uma conta?</p>
                <button onClick={() => setIsRegister(false)}
                  className="text-[#1CA1BA] hover:text-[#17889E] font-medium text-sm mt-1 transition-colors"
                  data-testid="go-to-login-btn">
                  Fazer login
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-slate-500 text-sm mt-6">
          CiaPonto Indica+ - Programa de Afiliados
        </p>
      </div>
    </div>
  );
}
